import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Clock, Phone } from 'lucide-react';

export const FamilyPhysicianAppointment: React.FC = () => {
  const appointmentTypes = [
    {
      title: "New Patient Visit",
      description: "Comprehensive first visit for new patients to establish care",
      duration: "45-60 minutes",
      preparation: "Please arrive 15 minutes early and bring completed new patient forms, insurance card, photo ID, and medication list."
    },
    {
      title: "Annual Physical",
      description: "Comprehensive yearly exam to assess overall health",
      duration: "30-45 minutes",
      preparation: "Please wear comfortable clothing. Fasting may be required for lab work."
    },
    {
      title: "Follow-Up Visit",
      description: "Follow-up appointment for existing conditions or concerns",
      duration: "15-30 minutes",
      preparation: "Bring any relevant medical records or test results."
    },
    {
      title: "Sick Visit",
      description: "Same-day or next-day appointment for acute illness or injury",
      duration: "15-30 minutes",
      preparation: "Please describe your symptoms when scheduling."
    },
    {
      title: "Telehealth Visit",
      description: "Virtual appointment conducted via secure video platform",
      duration: "15-30 minutes",
      preparation: "Ensure you have a reliable internet connection and private space for your appointment."
    },
    {
      title: "Wellness Check",
      description: "Preventive care visit focusing on health maintenance",
      duration: "30 minutes",
      preparation: "Consider health goals or concerns you'd like to discuss."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Book an Appointment</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Schedule your visit online or call our office. New patients are always welcome.
            </p>
          </div>
        </div>
      </section>

      {/* Appointment Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Appointment Types</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer various appointment types to meet your healthcare needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {appointmentTypes.map((type, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{type.title}</h3>
                <p className="text-gray-600 mb-4">{type.description}</p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-primary-600 mr-2 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Duration:</span>
                      <p className="text-sm text-gray-600">{type.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-700">Preparation:</span>
                      <p className="text-sm text-gray-600">{type.preparation}</p>
                    </div>
                  </div>
                </div>
                <a href="#booking-form" className="text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center">
                  Book This Appointment
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking-form" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Request an Appointment</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Complete the form below to request an appointment. Our staff will contact you to confirm 
                your appointment time. For same-day appointments or urgent needs, please call our office directly.
              </p>
              
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Before Your Visit:</h3>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                    Complete new patient forms if this is your first visit
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                    Bring your insurance card and photo ID
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                    Bring a list of current medications and dosages
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                    Arrive 15 minutes early to complete check-in
                  </li>
                </ul>
              </div>
              
              <div className="bg-primary-50 p-6 rounded-lg border border-primary-200">
                <div className="flex items-center mb-4">
                  <Phone className="h-6 w-6 text-primary-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Need a same-day appointment?</h3>
                </div>
                <p className="text-gray-600 mb-4">
                  For urgent needs or same-day appointments, please call our office directly at:
                </p>
                <a 
                  href="tel:5551234567" 
                  className="text-xl font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                >
                  (555) 123-4567
                </a>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Appointment Request Form</h3>
              <form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder="Your email"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="appointment-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Appointment Type
                  </label>
                  <select
                    id="appointment-type"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  >
                    <option value="">Select appointment type</option>
                    {appointmentTypes.map((type, index) => (
                      <option key={index} value={type.title.toLowerCase().replace(/\s+/g, '-')}>
                        {type.title}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="doctor" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Doctor
                  </label>
                  <select
                    id="doctor"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  >
                    <option value="">No preference</option>
                    <option value="dr-johnson">Dr. Sarah Johnson</option>
                    <option value="dr-chen">Dr. Michael Chen</option>
                    <option value="dr-rodriguez">Dr. Emily Rodriguez</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      id="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Time
                    </label>
                    <select
                      id="time"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                    >
                      <option value="">Select a time</option>
                      <option value="morning">Morning (8am-12pm)</option>
                      <option value="afternoon">Afternoon (12pm-5pm)</option>
                      <option value="evening">Evening (5pm-7pm)</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Visit
                  </label>
                  <textarea
                    id="reason"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                    placeholder="Please briefly describe the reason for your visit"
                  ></textarea>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">I'm a new patient</span>
                  </label>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                >
                  Request Appointment
                </button>
                
                <p className="text-xs text-gray-500 text-center">
                  This form is for appointment requests only. We'll contact you to confirm your appointment time.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What to Expect</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We want your visit to be as smooth and comfortable as possible. Here's what to expect when you visit our office.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-primary-600 font-bold text-xl">1</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Before Your Visit</h3>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Complete new patient forms (if applicable)</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Gather your insurance card, ID, and medication list</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Write down any questions or concerns</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Confirm your appointment time</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-primary-600 font-bold text-xl">2</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">During Your Visit</h3>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check in with our front desk staff</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Medical assistant will check vitals and review history</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Doctor will examine you and discuss concerns</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Treatment plan will be developed together</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-md">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-primary-600 font-bold text-xl">3</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">After Your Visit</h3>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Check out and schedule follow-up if needed</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Review your visit summary in the patient portal</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Pick up any prescriptions from your pharmacy</span>
                </li>
                <li className="flex items-start text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Follow your treatment plan and contact us with questions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Schedule Your Visit?</h2>
          <p className="text-xl text-primary-100 mb-8">
            We're accepting new patients and would be honored to care for you and your family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#booking-form" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Book Online
            </a>
            <a href="tel:5551234567" className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
              Call (555) 123-4567
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};